/**
 * Traceability Utilities
 * 
 * Centralized functions for extracting row numbers, dates, and amounts
 * from audit finding data_reference objects. This ensures consistent
 * traceability handling across UI components, reports, and exports.
 */

export interface AffectedTransaction {
  date?: string;
  amount?: number;
  row_index?: number;
  description?: string;
}

export interface DataReference {
  // Canonical fields (preferred)
  row_indices?: number[];
  dates?: string[];
  amounts?: number[];
  affected_transactions?: AffectedTransaction[];
  position_note?: string;
  
  // Legacy fields (fallback)
  dataReferences?: {
    rowNumbers?: number[];
    dates?: string[];
    amounts?: number[];
  };
  
  // Metrics (sometimes contains row_indices)
  metrics?: {
    row_indices?: number[];
    [key: string]: unknown;
  };
  
  // Allow additional fields
  [key: string]: unknown;
}

export interface NormalizedTraceability {
  rowIndices: number[];
  dates: string[];
  amounts: number[];
  affectedTransactions: AffectedTransaction[];
  positionNote: string | null;
}

/**
 * Normalize traceability data from a finding's data_reference.
 * 
 * Priority order:
 * 1. Canonical top-level fields (row_indices, dates, amounts)
 * 2. Derived from affected_transactions array
 * 3. Legacy dataReferences object
 * 4. Metrics object (row_indices only)
 * 
 * @param dataRef - The data_reference object from an audit finding
 * @returns Normalized traceability with rowIndices, dates, amounts, etc.
 */
export function normalizeTraceability(dataRef: DataReference | null | undefined): NormalizedTraceability {
  const result: NormalizedTraceability = {
    rowIndices: [],
    dates: [],
    amounts: [],
    affectedTransactions: [],
    positionNote: null,
  };

  if (!dataRef) {
    return result;
  }

  // Extract affected_transactions first (needed for fallback extraction)
  const affectedTxns = Array.isArray(dataRef.affected_transactions) 
    ? dataRef.affected_transactions 
    : [];
  result.affectedTransactions = affectedTxns;

  // Position note
  if (typeof dataRef.position_note === 'string' && dataRef.position_note.trim()) {
    result.positionNote = dataRef.position_note.trim();
  }

  // Row indices: canonical → affected_transactions → metrics → legacy
  if (Array.isArray(dataRef.row_indices) && dataRef.row_indices.length > 0) {
    result.rowIndices = dataRef.row_indices.filter((n): n is number => typeof n === 'number');
  } else if (affectedTxns.length > 0) {
    const fromTxns = affectedTxns
      .map(t => t.row_index)
      .filter((n): n is number => typeof n === 'number');
    if (fromTxns.length > 0) {
      result.rowIndices = [...new Set(fromTxns)].sort((a, b) => a - b);
    }
  } else if (dataRef.metrics?.row_indices && Array.isArray(dataRef.metrics.row_indices)) {
    result.rowIndices = dataRef.metrics.row_indices.filter((n): n is number => typeof n === 'number');
  } else if (dataRef.dataReferences?.rowNumbers && Array.isArray(dataRef.dataReferences.rowNumbers)) {
    result.rowIndices = dataRef.dataReferences.rowNumbers.filter((n): n is number => typeof n === 'number');
  }

  // Dates: canonical → affected_transactions → legacy
  if (Array.isArray(dataRef.dates) && dataRef.dates.length > 0) {
    result.dates = dataRef.dates.filter((d): d is string => typeof d === 'string' && d.trim() !== '');
  } else if (affectedTxns.length > 0) {
    const fromTxns = affectedTxns
      .map(t => t.date)
      .filter((d): d is string => typeof d === 'string' && d.trim() !== '');
    if (fromTxns.length > 0) {
      result.dates = [...new Set(fromTxns)].sort();
    }
  } else if (dataRef.dataReferences?.dates && Array.isArray(dataRef.dataReferences.dates)) {
    result.dates = dataRef.dataReferences.dates.filter((d): d is string => typeof d === 'string' && d.trim() !== '');
  }

  // Amounts: canonical → affected_transactions → legacy
  if (Array.isArray(dataRef.amounts) && dataRef.amounts.length > 0) {
    result.amounts = dataRef.amounts.filter((n): n is number => typeof n === 'number');
  } else if (affectedTxns.length > 0) {
    const fromTxns = affectedTxns
      .map(t => t.amount)
      .filter((n): n is number => typeof n === 'number');
    if (fromTxns.length > 0) {
      result.amounts = fromTxns;
    }
  } else if (dataRef.dataReferences?.amounts && Array.isArray(dataRef.dataReferences.amounts)) {
    result.amounts = dataRef.dataReferences.amounts.filter((n): n is number => typeof n === 'number');
  }

  return result;
}

/**
 * Format traceability for display (e.g., "Rad 5, 12, 18")
 */
export function formatRowIndices(rowIndices: number[], maxDisplay = 5): string {
  if (rowIndices.length === 0) return '';
  
  const sorted = [...rowIndices].sort((a, b) => a - b);
  if (sorted.length <= maxDisplay) {
    return `Rad ${sorted.join(', ')}`;
  }
  
  const shown = sorted.slice(0, maxDisplay);
  const remaining = sorted.length - maxDisplay;
  return `Rad ${shown.join(', ')} (+${remaining} till)`;
}

/**
 * Format dates for display (e.g., "2024-01-15, 2024-02-20")
 */
export function formatDates(dates: string[], maxDisplay = 3): string {
  if (dates.length === 0) return '';
  
  const sorted = [...dates].sort();
  if (sorted.length <= maxDisplay) {
    return sorted.join(', ');
  }
  
  const shown = sorted.slice(0, maxDisplay);
  const remaining = sorted.length - maxDisplay;
  return `${shown.join(', ')} (+${remaining} till)`;
}
